import React from "react";
import { components, type OptionProps, type SingleValueProps } from "react-select";

/** One row for the combined “Deposit To” select; payment API still receives name, bank, and account separately. */
export type DepositDestinationOption = {
  /** Stable id for react-select (beneficiary id from API, or deposit name for invoice-only row). */
  value: string;
  label: string;
  bankName: string;
  accountNumber: string;
  /** Value sent as `depositTo` on submit (beneficiary name). */
  depositToName: string;
  beneficiaryIBAN?: string;
  branch?: string;
  swiftCode?: string;
  address?: string;
};

export function getDepositToNameForPayload(option: DepositDestinationOption | null | undefined | string): string {
  if (!option || typeof option !== "object") return "";
  return option.depositToName || option.label || option.value;
}

function resolveBankName(row: Record<string, any> | undefined): string {
  if (!row) return "";
  const payableTo = row?.payableTo;
  if (payableTo === "owner") {
    return row?.beneficiaryBank || row?.depositToBank || "";
  }
  if (payableTo === "lessor") {
    return row?.lessorBankName || row?.depositToBank || "";
  }
  return row?.beneficiaryBank || row?.lessorBankName || row?.depositToBank || "";
}

function resolveAccountNumber(row: Record<string, any> | undefined): string {
  if (!row) return "";
  const payableTo = row?.payableTo;
  if (payableTo === "owner") {
    const v = row?.beneficiaryAccount ?? row?.depositToAccountNumber;
    return v != null && v !== "" ? String(v) : "";
  }
  if (payableTo === "lessor") {
    const v = row?.lessorAccountNumber ?? row?.depositToAccountNumber;
    return v != null && v !== "" ? String(v) : "";
  }
  const v = row?.beneficiaryAccount ?? row?.lessorAccountNumber ?? row?.depositToAccountNumber;
  return v != null && v !== "" ? String(v) : "";
}

export function buildDepositDestinationFromInvoice(
  row: Record<string, any> | undefined,
): DepositDestinationOption | null {
  if (!row) return null;
  const depositToName = row?.depositTo || row?.beneficiaryName;
  if (!depositToName) return null;

  return {
    value: depositToName,
    label: depositToName,
    bankName: resolveBankName(row),
    accountNumber: resolveAccountNumber(row),
    depositToName,
    beneficiaryIBAN: row?.beneficiaryIBAN,
    branch: row?.branch,
    swiftCode: row?.swiftCode,
    address: row?.address,
  };
}

function normalizeBeneficiaryBank(beneficiaryBank: unknown): string {
  if (beneficiaryBank && typeof beneficiaryBank === "object" && "label" in (beneficiaryBank as object)) {
    return String((beneficiaryBank as { label?: string }).label ?? "");
  }
  return String(beneficiaryBank ?? "");
}

/** Maps GET `/property/beneficiary` rows into select options (same bank/account/name sent on payment as today). */
export function mapBeneficiaryApiToDepositOptions(rows: Record<string, any>[]): DepositDestinationOption[] {
  if (!rows?.length) return [];
  return rows
    .filter((item) => !item?.status || item.status === "enable" || item.status === "active")
    .map((item) => {
      const depositToName = String(item?.beneficiaryName ?? "").trim();
      const bankName = normalizeBeneficiaryBank(item?.beneficiaryBank);
      const accountNumber = String(item?.beneficiaryAccountNumber ?? item?.accountNumber ?? "").trim();
      const id = String(item?.beneficiaryId ?? item?._id ?? "").trim() || `${depositToName}-${accountNumber}`;
      const branch = String(item?.branch ?? "").trim();
      const swiftCode = String(item?.swiftCode ?? "").trim();
      const address = String(item?.address ?? "").trim();
      return {
        value: id,
        label: depositToName || id,
        bankName,
        accountNumber,
        depositToName: depositToName || id,
        beneficiaryIBAN: item?.beneficiaryIBAN ?? item?.iban,
        branch,
        swiftCode,
        address,
      };
    })
    .filter((o) => o.depositToName);
}

function DepositDestinationLabelBody({
  option,
  sublineColor,
  titleColor,
  menuPadding,
}: {
  option: DepositDestinationOption;
  sublineColor: string;
  titleColor?: string;
  menuPadding?: boolean;
}) {
  const sublineText = [option.bankName, option.accountNumber].filter(Boolean).join(" • ");
  /** In the dropdown, every row keeps two lines so hover/selected heights match. */
  const isMenuRow = Boolean(menuPadding);
  const showSecondLine = isMenuRow || Boolean(sublineText);

  return (
    <div
      style={{
        padding: menuPadding ? "1px 0" : 0,
        ...(isMenuRow ? { minHeight: 36, display: "flex", flexDirection: "column", justifyContent: "center" } : {}),
      }}
    >
      <div
        style={{
          fontWeight: 700,
          textTransform: "uppercase",
          lineHeight: 1.15,
          ...(isMenuRow ? { fontSize: 12 } : {}),
          ...(titleColor ? { color: titleColor } : {}),
        }}
      >
        {option.label || option.value}
      </div>
      {showSecondLine ? (
        <div
          style={{
            fontSize: 12,
            color: sublineColor,
            lineHeight: 1.25,
            textTransform: "none",
            minHeight: isMenuRow ? 16 : undefined,
          }}
        >
          {sublineText || (isMenuRow ? "\u00a0" : "")}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Menu row: match Payment Mode react-select — selected = primary grey + white text;
 * hover = light blue + inherited dark text from option styles; default = dark text.
 */
export function DepositDestinationSelectOption(props: OptionProps<DepositDestinationOption, false>) {
  const { data, isSelected } = props;
  const titleColor = isSelected ? "#ffffff" : undefined;
  const sublineColor = isSelected ? "#ffffff" : "inherit";
  return (
    <components.Option {...props}>
      <DepositDestinationLabelBody option={data} sublineColor={sublineColor} titleColor={titleColor} menuPadding />
    </components.Option>
  );
}

/** Closed control: muted subline on the default light control. */
export function DepositDestinationSingleValue(props: SingleValueProps<DepositDestinationOption, false>) {
  return (
    <components.SingleValue {...props}>
      <DepositDestinationLabelBody option={props.data} sublineColor="rgba(0, 0, 0, 0.45)" />
    </components.SingleValue>
  );
}

export const depositDestinationSelectComponents = {
  Option: DepositDestinationSelectOption,
  SingleValue: DepositDestinationSingleValue,
};
